// Sistema di autenticazione con URL dinamici e token JWT

interface AuthToken {
  token: string
  expiresAt: number
}

interface LoginUrl {
  url: string
  expiresAt: number
}

class AuthSystem {
  private static instance: AuthSystem
  private currentLoginUrl: LoginUrl | null = null
  private authToken: AuthToken | null = null
  private readonly URL_VALIDITY_HOURS = 12
  private readonly TOKEN_VALIDITY_HOURS = 2
  private readonly SECRET_KEY = 'museo-genio-secret-2024'

  private constructor() {
    this.loadFromStorage()
    this.generateLoginUrlIfNeeded()
  }

  static getInstance(): AuthSystem {
    if (!AuthSystem.instance) {
      AuthSystem.instance = new AuthSystem()
    }
    return AuthSystem.instance
  }

  // Genera un URL di login valido per 12 ore
  private generateLoginUrl(): string {
    const timestamp = Date.now()
    const validUntil = timestamp + (this.URL_VALIDITY_HOURS * 60 * 60 * 1000)
    
    // Crea un hash basato su timestamp e secret
    const hash = this.createHash(`${timestamp}-${this.SECRET_KEY}`)
    
    // Ottieni il base path dall'URL corrente
    const basePath = this.getBasePath()
    const loginUrl = `${basePath}/login/${hash}`
    
    this.currentLoginUrl = {
      url: loginUrl,
      expiresAt: validUntil
    }
    
    this.saveToStorage()
    return loginUrl
  }

  // Genera un nuovo URL di login (pubblico)
  generateNewLoginUrl(): string {
    return this.generateLoginUrl()
  }

  // Genera URL di login con reindirizzamento post-autenticazione
  generateLoginUrlWithRedirect(redirectUrl: string): string {
    const baseLoginUrl = this.generateLoginUrl()
    const separator = baseLoginUrl.includes('?') ? '&' : '?'
    return `${baseLoginUrl}${separator}redirect=${encodeURIComponent(redirectUrl)}`
  }

  // Ottieni il base path dell'applicazione
  private getBasePath(): string {
    try {
      // Prova a ottenere il base path dal meta tag 8thwall:project
      const projectMeta = document.querySelector('meta[name="8thwall:project"]')
      if (projectMeta && projectMeta.getAttribute('content')) {
        const projectName = projectMeta.getAttribute('content')
        return `/${projectName}`
      }
      
      // Fallback: estrai il base path dall'URL corrente
      const pathParts = window.location.pathname.split('/')
      if (pathParts.length > 1 && pathParts[1]) {
        return `/${pathParts[1]}`
      }
      
      return ''
    } catch (error) {
      console.error('Errore nell\'estrazione del base path:', error)
      return ''
    }
  }

  // Verifica se l'URL di login è valido
  isLoginUrlValid(url: string): boolean {
    // Estrai solo la parte del path dall'URL completo
    const urlPath = this.extractPathFromUrl(url)
    
    // Rimuovi eventuali trailing slash per il confronto
    const normalizedUrlPath = urlPath.replace(/\/$/, '')
    
    // Se abbiamo un URL di login corrente, verificalo
    if (this.currentLoginUrl) {
      if (Date.now() > this.currentLoginUrl.expiresAt) {
        this.currentLoginUrl = null
        this.saveToStorage()
      } else {
        const expectedPath = this.currentLoginUrl.url
        const normalizedExpectedPath = expectedPath.replace(/\/$/, '')
        
        if (normalizedUrlPath === normalizedExpectedPath) {
          console.log('🔍 Verifica URL login: VALIDO (URL corrente)', {
            inputUrl: url,
            extractedPath: urlPath,
            normalizedUrlPath,
            expectedPath: expectedPath,
            normalizedExpectedPath,
            isValid: true
          })
          return true
        }
      }
    }
    
    // Se non abbiamo un URL corrente o non corrisponde, 
    // verifica se l'URL ha un formato valido e non è scaduto
    const isValidFormat = this.isValidLoginUrlFormat(normalizedUrlPath)
    
    console.log('🔍 Verifica URL login:', {
      inputUrl: url,
      extractedPath: urlPath,
      normalizedUrlPath,
      isValidFormat,
      hasCurrentUrl: !!this.currentLoginUrl,
      currentUrlExpired: this.currentLoginUrl ? Date.now() > this.currentLoginUrl.expiresAt : false
    })
    
    return isValidFormat
  }

  // Verifica se l'URL ha un formato valido per il login
  private isValidLoginUrlFormat(urlPath: string): boolean {
    // Verifica se l'URL segue il pattern /[base-path]/login/[hash] oppure /login/[hash]
    // Accetta sia con che senza base path per compatibilità
    const loginPatternWithBase = /^\/[^\/]+\/login\/[a-zA-Z0-9]+$/
    const loginPatternWithoutBase = /^\/login\/[a-zA-Z0-9]+$/
    return loginPatternWithBase.test(urlPath) || loginPatternWithoutBase.test(urlPath)
  }

  // Estrai il path dall'URL completo
  private extractPathFromUrl(url: string): string {
    try {
      // Se è già un path relativo, restituiscilo
      if (url.startsWith('/')) {
        return url
      }
      
      // Se è un URL completo, estrai il path
      const urlObj = new URL(url)
      return urlObj.pathname
    } catch (error) {
      // Se non è un URL valido, prova a estrarre il path manualmente
      const pathMatch = url.match(/\/[^?]*/)
      return pathMatch ? pathMatch[0] : url
    }
  }

  // Genera un token JWT per l'accesso
  generateAuthToken(): string {
    const timestamp = Date.now()
    const expiresAt = timestamp + (this.TOKEN_VALIDITY_HOURS * 60 * 60 * 1000)
    
    // Crea un payload semplice
    const payload = {
      userId: 'museo-user',
      issuedAt: timestamp,
      expiresAt: expiresAt
    }
    
    // Simula un JWT (in produzione usare una libreria JWT)
    const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }))
    const payloadEncoded = btoa(JSON.stringify(payload))
    const signature = this.createHash(`${header}.${payloadEncoded}.${this.SECRET_KEY}`)
    
    const token = `${header}.${payloadEncoded}.${signature}`
    
    this.authToken = {
      token: token,
      expiresAt: expiresAt
    }
    
    this.saveToStorage()
    return token
  }

  // Verifica se il token è valido
  isTokenValid(token?: string): boolean {
    const tokenToCheck = token || this.authToken?.token
    
    if (!tokenToCheck) {
      return false
    }
    
    try {
      const parts = tokenToCheck.split('.')
      if (parts.length !== 3) {
        return false
      }
      
      const payload = JSON.parse(atob(parts[1]))
      
      if (Date.now() > payload.expiresAt) {
        this.authToken = null
        this.saveToStorage()
        return false
      }
      
      return true
    } catch (error) {
      console.error('Errore nella verifica del token:', error)
      return false
    }
  }

  // Ottieni l'URL di login corrente o ne genera uno nuovo
  getCurrentLoginUrl(): string {
    this.generateLoginUrlIfNeeded()
    return this.currentLoginUrl?.url || this.generateLoginUrl()
  }

  // Ottieni l'URL di login completo (con dominio)
  getCurrentLoginUrlFull(): string {
    const baseUrl = window.location.origin
    const basePath = this.getBasePath()
    const loginPath = this.getCurrentLoginUrl()
    
    // Se il loginPath già include il base path, non duplicarlo
    if (loginPath.startsWith(basePath)) {
      return `${baseUrl}${loginPath}`
    }
    
    return `${baseUrl}${basePath}${loginPath}`
  }

  // Genera un nuovo URL se quello corrente è scaduto
  private generateLoginUrlIfNeeded(): void {
    if (!this.currentLoginUrl || Date.now() > this.currentLoginUrl.expiresAt) {
      this.generateLoginUrl()
    }
  }

  // Crea un hash semplice (in produzione usare crypto)
  private createHash(input: string): string {
    let hash = 0
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36)
  }

  // Salva i dati nel localStorage
  private saveToStorage(): void {
    try {
      localStorage.setItem('museo_auth_token', JSON.stringify(this.authToken))
      localStorage.setItem('museo_login_url', JSON.stringify(this.currentLoginUrl))
    } catch (error) {
      console.error('Errore nel salvataggio dei dati di autenticazione:', error)
    }
  }

  // Carica i dati dal localStorage
  private loadFromStorage(): void {
    try {
      const tokenData = localStorage.getItem('museo_auth_token')
      const urlData = localStorage.getItem('museo_login_url')
      
      if (tokenData) {
        this.authToken = JSON.parse(tokenData)
      }
      
      if (urlData) {
        this.currentLoginUrl = JSON.parse(urlData)
      }
    } catch (error) {
      console.error('Errore nel caricamento dei dati di autenticazione:', error)
    }
  }

  // Logout
  logout(): void {
    this.authToken = null
    this.saveToStorage()
  }

  // Ottieni informazioni sullo stato dell'autenticazione
  getAuthStatus(): { isAuthenticated: boolean, tokenExpiresAt?: number, urlExpiresAt?: number } {
    const isAuthenticated = this.isTokenValid()
    
    return {
      isAuthenticated,
      tokenExpiresAt: this.authToken?.expiresAt,
      urlExpiresAt: this.currentLoginUrl?.expiresAt
    }
  }
}

export { AuthSystem } 